import { fetch, NetworkError } from "@self/fetch";
import { noticeFailure } from "@self/notices";
import { Result } from "@self/Result";
import { ObservedAttributes, showMinicart } from "@self/utils";
import { bound, CustomElement, customElement } from "../CustomElement";
import { Quantity } from "../Quantity";

type FormDataFilter = (formData: FormData) => void | PromiseLike<void>;

@customElement("gct-add-to-cart")
export class AddToCart extends CustomElement {

	static get observedAttributes() {
		return ["min", "max", "value", "disabled"] as const;
	}

	quantity: Quantity;
	button: HTMLButtonElement;

	filters: FormDataFilter[];

	constructor() {
		const quantity = <gct-quantity className="gct-flex-row" /> as Quantity;
		const button = <button type="button" className="gct-btn-lg">Add to basket</button> as HTMLButtonElement;

		super(
			<div className="gct-flex-column gct-gap-16">
				<div className="gct-flex-column gct-gap-8">
					<span className="gct-quantity-mobile">Quanity:</span>
					<div className="gct-pr-16 gct-pl-16 gct-pt-16 gct-pb-16 gct-border-400 gct-content-fit">
						{quantity}
					</div>
				</div>
				{button}
			</div>
		);

		const min = this.getAttribute("min");
		const max = this.getAttribute("max");
		const value = this.getAttribute("value");
		const disabled = this.getAttribute("disabled");

		if (min !== null) {
			quantity.setAttribute("min", min);
		}
		if (max !== null) {
			quantity.setAttribute("max", max);
		}
		if (value !== null) {
			quantity.setAttribute("value", value);
		}
		if (disabled !== null) {
			quantity.setAttribute("disabled", "");
			button.setAttribute("disabled", "");
		}

		this.quantity = quantity;
		this.button = button;

		this.filters = [];
	}

	connectedCallback() {
		this.button.addEventListener("click", this.onClick);
	}

	disconnectedCallback() {
		this.button.removeEventListener("click", this.onClick);
	}

	attributeChangedCallback(name: ObservedAttributes<typeof AddToCart>, _oldValue: string | null, newValue: string | null) {
		switch (name) {
			case "disabled":
				if (newValue !== null) {
					this.button.setAttribute(name, newValue);
				} else {
					this.button.removeAttribute(name);
				}
			// fallthrough
			case "min":
			case "max":
			case "value":
				if (newValue !== null) {
					this.quantity.setAttribute(name, newValue);
				} else {
					this.quantity.removeAttribute(name);
				}
				break;
		}
	}

	@bound
	async onClick() {
		try {
			this.button.disabled = true;

			const data = new FormData();
			data.set("product_id", this.dataset.variationId ?? this.dataset.productId!);
			data.set("quantity", String(this.quantity.value));

			for (const filter of this.filters) {
				await Promise.resolve(filter(data));
			}

			const json = await Result.flatMapAsync(
				fetch(this.dataset.endpoint!, {
					method: "POST",
					body: data,
					credentials: "include",
				}),
				(res) => res.json(),
			);

			json.match({
				onSuccess: (json) => {
					/* TODO In case of an error, we get object in the shape of:
					 * { error: true, product_url: string }
					 * When that happens, we need to fetch the product URL,
					 * extract any notices from it and then show them.
					 */
					const fragment = new DOMParser().parseFromString((json as any).fragments["div.widget_shopping_cart_content"], "text/html");
					const children = fragment.children[0].children;

					const minicart = document.getElementById("mini-cart-container");
					if (minicart !== null) {
						minicart.replaceChildren(...children);
						showMinicart();
					}
				},
				onFailure: (error) => {
					console.error(error);
					if (error instanceof NetworkError) {
						noticeFailure("Couldn't connect. Are you online?");
					}
				},
			});
		} catch (error) {
			noticeFailure("There was an unexpected error.");
		} finally {
			this.button.disabled = false;
		}
	}
}
